import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Avatar,
    ListItemButton,
} from "@mui/material";
import {
    ArrowBackIos as BackIcon,
    ExpandMore as ExpandMoreIcon,
    Info as InfoIcon,
    LocationOn as LocationIcon,
    Business as CompanyIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import ContactService from "../services/Contact.service";

function LabelValue({ label, value }) {
    if (!value && value !== 0) return null;
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
            >
                {label}
            </Typography>
            <Typography variant="body2">{value}</Typography>
        </Box>
    );
}

function initialsOf(name) {
    return name
        ? name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
        : "?";
}

function ContactDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [contact, setContact] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [expanded, setExpanded] = useState("info");

    useEffect(() => {
        setLoading(true);
        setError("");
        ContactService.get(id)
            .then((res) => setContact(res.data))
            .catch(() => setError("Failed to load contact details."))
            .finally(() => setLoading(false));
    }, [id]);

    const toggle = (panel) => (_, isOpen) => setExpanded(isOpen ? panel : false);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !contact) {
        return (
            <Box>
                <IconButton size="small" onClick={() => navigate(-1)} sx={{ mb: 1 }}>
                    <BackIcon fontSize="small" />
                </IconButton>
                <Alert severity="error">{error || "Contact not found."}</Alert>
            </Box>
        );
    }

    const phones = Array.isArray(contact.phone) ? contact.phone : [];
    const addr = contact.address;

    return (
        <Box>
            {/* ── Header ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <IconButton size="small" onClick={() => navigate(-1)}>
                    <BackIcon fontSize="small" />
                </IconButton>
                <Avatar sx={{ width: 40, height: 40, bgcolor: "primary.main" }}>
                    {initialsOf(contact.name)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{ fontFamily: "Montserrat, sans-serif" }}
                            noWrap
                        >
                            {contact.name}
                        </Typography>
                        {contact.is_archived && <Chip label="Archived" size="small" />}
                    </Box>
                    {contact.job_title && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {contact.job_title}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* ── Contact Info ── */}
            <Accordion
                expanded={expanded === "info"}
                onChange={toggle("info")}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <InfoIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Contact Information
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    {contact.email && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5 }}>
                            <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            <Typography
                                component="a"
                                href={`mailto:${contact.email}`}
                                variant="body2"
                                color="primary"
                                sx={{ textDecoration: "none" }}
                            >
                                {contact.email}
                            </Typography>
                        </Box>
                    )}
                    {phones.map((ph) => (
                        <Box key={ph.id} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
                            <PhoneIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            <Typography
                                component="a"
                                href={`tel:${ph.phone_number}`}
                                variant="body2"
                                color="primary"
                                sx={{ textDecoration: "none" }}
                            >
                                {ph.phone_number}
                            </Typography>
                            {ph.phone_type && (
                                <Typography variant="caption" color="text.secondary">
                                    · {ph.phone_type}
                                </Typography>
                            )}
                        </Box>
                    ))}
                    {contact.fax && <LabelValue label="Fax" value={contact.fax.phone_number} />}
                    {!contact.email && phones.length === 0 && !contact.fax && (
                        <Typography variant="body2" color="text.secondary">
                            No contact details on file.
                        </Typography>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* ── Company ── */}
            {contact.company && (
                <Accordion
                    expanded={expanded === "company"}
                    onChange={toggle("company")}
                    disableGutters
                    elevation={0}
                    sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CompanyIcon fontSize="small" color="primary" />
                            <Typography variant="subtitle2" fontWeight={600}>
                                Company
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                        <ListItemButton
                            onClick={() => navigate(`/crm/companies/${contact.company.id}`)}
                            sx={{ py: 1, px: 2 }}
                        >
                            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                                {contact.company.name}
                            </Typography>
                            <ChevronRightIcon fontSize="small" color="action" />
                        </ListItemButton>
                    </AccordionDetails>
                </Accordion>
            )}

            {/* ── Address ── */}
            {addr && (
                <Accordion
                    expanded={expanded === "address"}
                    onChange={toggle("address")}
                    disableGutters
                    elevation={0}
                    sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LocationIcon fontSize="small" color="primary" />
                            <Typography variant="subtitle2" fontWeight={600}>
                                Address
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2">{addr.address}</Typography>
                        {addr.address_line2 && <Typography variant="body2">{addr.address_line2}</Typography>}
                        <Typography variant="body2">
                            {[addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ")}
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            )}
        </Box>
    );
}

export default ContactDetail;
